import { useLanguage, LanguageProvider } from "@/shared/components/shared/LanguageContext";

import Landing from "@/shared/pages/Landing";
import Pricing from "@/shared/pages/Pricing";
import AboutUs from "@/shared/pages/AboutUs";
import Careers from "@/shared/pages/Careers";
import APIDocumentation from "@/shared/pages/APIDocumentation";
import Integrations from "@/shared/pages/Integrations";
import Blog from "@/shared/pages/Blog";
import Contact from "@/shared/pages/Contact";
import PrivacyPolicy from "@/shared/pages/PrivacyPolicy";
import TermOfServices from "@/shared/pages/TermOfServices";
import Compliance from "@/shared/pages/Compliance";
import Security from "@/shared/pages/Security";

import { BrowserRouter as Router, Route, Routes, useLocation, Navigate, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button } from "@/shared/components/ui/button";
import { Lock, Menu, X } from "lucide-react";
import { Toaster } from "@/shared/components/ui/toaster";
import WelcomeAnimation from "@/shared/components/shared/WelcomeAnimation";
import logo from "@/assets/logo.png";

const PAGES = {
  Landing
};

function _getCurrentPage(url) {
  if (url.endsWith('/')) {
    url = url.slice(0, -1);
  }
  let urlLastPart = url.split('/').pop();
  if (urlLastPart.includes('?')) {
    urlLastPart = urlLastPart.split('?')[0];
  }

  const pageName = Object.keys(PAGES).find(page => page.toLowerCase() === urlLastPart.toLowerCase());
  return pageName;
}

const Layout = ({ children }) => {
  const location = useLocation();
  const isHome = location.pathname === '/' || location.pathname === '' || location.pathname.toLowerCase().includes('home');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-slate-950 text-white overflow-x-hidden flex flex-col">
      <style>{`
        .gradient-text {
          background: linear-gradient(135deg, #60a5fa 0%, #a78bfa 50%, #f472b6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .hero-gradient {
          background: radial-gradient(ellipse 80% 50% at 50% -20%, rgba(120, 119, 198, 0.3), transparent);
        }
        .card-glow:hover {
          box-shadow: 0 0 40px rgba(96, 165, 250, 0.15);
        }
        .grid-pattern {
          background-image:
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 60px 60px;
        }
      `}</style>
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div
              className="flex items-center gap-1 cursor-pointer"
              onClick={() => window.location.href = 'https://panel.luxkgate.com/'}
            >
              <img src={logo} alt="Logo" className="h-8 w-auto object-contain" />
              <span className="font-bold text-xl tracking-tight">Luxkgate</span>
            </div>

            {isHome && (
              <div className="hidden md:flex items-center gap-8">
                <a href="#features" className="text-slate-400 hover:text-white transition-colors text-sm">Features</a>
                <a href="#docs" className="text-slate-400 hover:text-white transition-colors text-sm">Documentation</a>
                <a href="#contact" className="text-slate-400 hover:text-white transition-colors text-sm">Contact</a>
              </div>
            )}

            <div className="hidden md:flex items-center gap-3">
              <Button
                className="bg-transparent text-slate-400 hover:text-white hover:bg-slate-800 border-0 transition-colors"
                onClick={() => window.location.href = 'https://agent.panel.luxkgate.com/login'}
              >
                Sign In
              </Button>
              <Button
                className="bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700 text-white border-0 transition-all hover:shadow-lg hover:shadow-blue-500/25"
                onClick={() => window.location.href = 'https://agent.panel.luxkgate.com/login'}
              >
                Get Started
              </Button>
            </div>

            <div className="flex items-center md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-slate-400 hover:text-white focus:outline-none"
              >
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-slate-900 border-b border-slate-800/50">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {isHome && (
                <>
                  <a href="#features" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-base font-medium text-slate-400 hover:text-white hover:bg-slate-800 rounded-md">Features</a>
                  <a href="#docs" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-base font-medium text-slate-400 hover:text-white hover:bg-slate-800 rounded-md">Documentation</a>
                  <a href="#contact" onClick={() => setIsMobileMenuOpen(false)} className="block px-3 py-2 text-base font-medium text-slate-400 hover:text-white hover:bg-slate-800 rounded-md">Contact</a>
                </>
              )}
              <div className="flex flex-col gap-3 px-3 py-4 mt-2 border-t border-slate-800">
                <Button
                  className="w-full justify-center bg-transparent text-slate-300 hover:text-white border border-slate-700 hover:bg-slate-800 transition-colors"
                  onClick={() => window.location.href = 'https://admin.panel.luxkgate.com/AdminLogin'}
                >
                  Sign In
                </Button>
                <Button
                  className="w-full justify-center bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700 text-white border-0 transition-all hover:shadow-lg hover:shadow-blue-500/25"
                  onClick={() => window.location.href = 'https://admin.panel.luxkgate.com/AdminDashboard'}
                >
                  Get Started
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>
      <main className="flex-1 flex flex-col">
        {children}
      </main>
      <Toaster />
      <footer className="border-t border-slate-800/50 py-12 px-4 mt-auto">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div
                className="flex items-center gap-1 mb-4 cursor-pointer"
                onClick={() => window.location.href = 'https://panel.luxkgate.com/'}
              >
                <img src={logo} alt="Logo" className="h-8 w-auto object-contain" />
                <span className="font-bold text-xl">Luxkgate</span>
              </div>
              <p className="text-slate-500 text-sm">
                Enterprise payment gateway for the modern era.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2">
                <li><Link to="/pricing" className="text-slate-500 hover:text-white text-sm transition-colors">Pricing</Link></li>
                <li><Link to="https://docs.panel.luxkgate.com/" className="text-slate-500 hover:text-white text-sm transition-colors">API Documentation</Link></li>
                <li><Link to="/integrations" className="text-slate-500 hover:text-white text-sm transition-colors">Integrations</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-slate-500 hover:text-white text-sm transition-colors">About Us</Link></li>
                <li><Link to="/careers" className="text-slate-500 hover:text-white text-sm transition-colors">Careers</Link></li>
                <li><Link to="/blog" className="text-slate-500 hover:text-white text-sm transition-colors">Blog</Link></li>
                <li><Link to="/contact" className="text-slate-500 hover:text-white text-sm transition-colors">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy-policy" className="text-slate-500 hover:text-white text-sm transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="text-slate-500 hover:text-white text-sm transition-colors">Terms of Service</Link></li>
                <li><Link to="/compliance" className="text-slate-500 hover:text-white text-sm transition-colors">Compliance</Link></li>
                <li><Link to="/security" className="text-slate-500 hover:text-white text-sm transition-colors">Security</Link></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-800/50 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Luxkgate. All rights reserved.
            </p>
            <div className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-slate-600" />
              <span className="text-slate-500 text-sm">PCI DSS Level 1 Compliant</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}


function HomeAppContent() {
  const location = useLocation();
  const currentPage = _getCurrentPage(location.pathname);

  return (
    <Layout>
      <Routes>
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/api-docs" element={<APIDocumentation />} />
        <Route path="/integrations" element={<Integrations />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermOfServices />} />
        <Route path="/compliance" element={<Compliance />} />
        <Route path="/security" element={<Security />} />
        <Route path="*" element={<Landing />} />
      </Routes>
    </Layout>
  );
}

export default function HomeApp() {
  return (
    <Router>
      <WelcomeAnimation />
      <HomeAppContent />
    </Router>
  );
}

